import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import { useControlCash, CURRENCY_OPTIONS } from "../../store/controlCashStore";
import logoImg from "../../assets/ControlCahsLogo.png";
import Avatar from "../ui/Avatar";
import { useAuth } from "../../contexts/AuthContext";
import { useTheme } from "../../contexts/ThemeContext";
import { useToast } from "../../contexts/ToastContext";

const Layout = ({ children }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { isDark, toggleDark } = useTheme();
  const { state, dispatch, hasExchangeRateApiKey, refreshExchangeRate, getCachedRateInfo } = useControlCash();
  const toast = useToast();
  const [showCurrencyMenu, setShowCurrencyMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setShowUserMenu(false);
      }
    };
    if (showUserMenu) document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [showUserMenu]);

  const [showRateInput, setShowRateInput] = useState(false);
  const [showVefPopup, setShowVefPopup] = useState(false);
  const [vefTempRate, setVefTempRate] = useState("36");
  const [isRefreshingRate, setIsRefreshingRate] = useState(false);
  const currentRate = state.exchangeRates?.[state.currency] ?? (state.currency === "PEN" ? state.usdToPenRate : 36);
  const [tempRate, setTempRate] = useState(String(currentRate));

  const enabledCurrencies = state.enabledCurrencies ?? ["PEN", "USD"];
  const currencyOptions = CURRENCY_OPTIONS.filter(o => enabledCurrencies.includes(o.code));

  const cachedInfo = getCachedRateInfo();
  const currentSymbol = CURRENCY_OPTIONS.find(o => o.code === state.currency)?.symbol ?? "S/";
  const isVef = state.currency === "VEF";

  const handleSetCurrency = (code) => {
    if (code === "VEF") {
      setVefTempRate(String(state.exchangeRates?.VEF ?? 36));
      setShowVefPopup(true);
      return;
    }
    dispatch({ type: "SET_CURRENCY", payload: code });
    setShowCurrencyMenu(false);
  };

  const handleVefPopupSave = () => {
    const rate = parseFloat(vefTempRate);
    if (!isNaN(rate) && rate > 0) {
      dispatch({ type: "SET_EXCHANGE_RATES", payload: { ...state.exchangeRates, VEF: rate } });
      dispatch({ type: "SET_CURRENCY", payload: "VEF" });
      setShowVefPopup(false);
      setShowCurrencyMenu(false);
    } else {
      toast.error("Ingresa un valor válido mayor a 0");
    }
  };

  const handleVefPopupCancel = () => {
    setShowVefPopup(false);
  };

  const handleOpenRateInput = () => {
    const rate = state.exchangeRates?.[state.currency] ?? (state.currency === "PEN" ? state.usdToPenRate : 36);
    setTempRate(String(rate));
    setShowRateInput(true);
  };

  const handleSaveRate = () => {
    const rate = parseFloat(tempRate);
    if (!isNaN(rate) && rate > 0) {
      const code = state.currency;
      dispatch({ type: "SET_EXCHANGE_RATES", payload: { ...state.exchangeRates, [code]: rate } });
      setShowRateInput(false);
    }
  };

  const handleRefreshRate = async () => {
    if (!hasExchangeRateApiKey) return;
    setIsRefreshingRate(true);
    const ok = await refreshExchangeRate();
    setIsRefreshingRate(false);
    if (!ok) {
      toast.error("No se pudo actualizar el tipo de cambio. Verifica tu conexión o API key.");
    }
  };

  const formatRateDate = (timestamp) => {
    if (!timestamp) return null;
    const d = new Date(timestamp);
    const now = new Date();
    const diffHours = (now - d) / (1000 * 60 * 60);
    if (diffHours < 1) return "hace un momento";
    if (diffHours < 24) return `hace ${Math.floor(diffHours)} h`;
    return d.toLocaleDateString("es-PE", { day: "2-digit", month: "short" });
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 transition-colors">
      <nav className="sticky top-0 z-50 bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl border-b border-gray-200/50 dark:border-gray-800/50 px-6 md:px-8 py-5 transition-colors">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => { setShowMobileMenu(!showMobileMenu); setShowCurrencyMenu(false); setShowUserMenu(false); }}
              className="md:hidden p-2 -ml-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors"
              aria-label="Abrir menú"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <button
              className="flex items-center gap-3 bg-transparent border-none cursor-pointer"
              onClick={() => navigate("/")}
            >
              <img src={logoImg} alt="ControlCash" className="h-11 w-auto" />
              <span className="text-xl font-bold tracking-tight bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                ControlCash
              </span>
            </button>
          </div>
          
          <div className="hidden md:flex items-center gap-4">
            <button 
              onClick={() => navigate("/savings")}
              className="flex items-center gap-2 px-4 py-2 rounded-xl transition-colors text-sm font-medium bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Metas de Ahorro</span>
            </button>
            <button 
              onClick={() => navigate("/recurring-incomes")}
              className="flex items-center gap-2 px-4 py-2 rounded-xl transition-colors text-sm font-medium bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Ingresos Recurrentes</span>
            </button>
            <button 
              onClick={() => navigate("/recurring")}
              className="flex items-center gap-2 px-4 py-2 rounded-xl transition-colors text-sm font-medium bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <span>Gastos Recurrentes</span>
            </button>
          </div>

          <div className="flex items-center gap-2 md:gap-3">
            {/* Selector de moneda - solo desktop */}
            <div className="relative hidden md:block">
              <button
                onClick={() => { setShowCurrencyMenu(!showCurrencyMenu); setShowMobileMenu(false); setShowUserMenu(false); }}
                className="px-3 md:px-4 py-2 bg-gray-100 dark:bg-gray-800 rounded-xl flex items-center gap-2 hover:bg-gray-200 dark:hover:bg-gray-700 transition-all text-sm"
                title="Cambiar moneda"
              >
                <span className="font-semibold text-gray-900 dark:text-gray-100">
                  {CURRENCY_OPTIONS.find(o => o.code === state.currency)?.symbol ?? state.currency}
                </span>
                <svg className="w-4 h-4 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {showCurrencyMenu && (
                <div className="absolute right-0 top-full mt-2 w-56 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 py-2 z-50">
                  <div className="px-4 py-2 border-b border-gray-100">
                    <p className="text-xs font-semibold text-gray-500 uppercase">Moneda</p>
                  </div>
                  {currencyOptions.map((opt) => (
                    <button
                      key={opt.code}
                      onClick={() => handleSetCurrency(opt.code)}
                      className={`w-full px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center justify-between ${state.currency === opt.code ? "bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300" : "text-gray-700 dark:text-gray-200"}`}
                    >
                      <span>{opt.label}</span>
                      {state.currency === opt.code && (
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </button>
                  ))}
                  <div className="border-t border-gray-100 dark:border-gray-700 mt-2 pt-2 px-4 space-y-2">
                    {state.currency !== "USD" && (
                      showRateInput ? (
                        <div className="space-y-2">
                          <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400">1 USD = {currentSymbol}</label>
                          <input
                            type="number"
                            step={isVef ? "1" : "0.01"}
                            min="0.01"
                            value={tempRate}
                            onChange={(e) => setTempRate(e.target.value)}
                            placeholder={isVef ? "Ej: 36" : "Ej: 3.75"}
                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                          <div className="flex gap-2">
                            <button onClick={handleSaveRate} className="flex-1 py-1.5 bg-blue-600 text-white text-sm rounded-lg">Guardar</button>
                            <button onClick={() => setShowRateInput(false)} className="py-1.5 text-gray-600 text-sm">Cancelar</button>
                          </div>
                          {isVef && (
                            <p className="text-xs text-gray-500 dark:text-gray-400">Bolívares: ingresa el valor manualmente (1 USD = Bs)</p>
                          )}
                        </div>
                      ) : (
                        <>
                          <button
                            onClick={handleOpenRateInput}
                            className="block text-xs text-gray-500 hover:text-blue-600 text-left w-full"
                          >
                            Tipo de cambio: 1 USD = {currentSymbol} {Number(currentRate).toLocaleString("es-PE", { minimumFractionDigits: isVef ? 0 : 2, maximumFractionDigits: isVef ? 0 : 2 })}
                          </button>
                          {!isVef && cachedInfo?.fetchedAt && (
                            <p className="text-xs text-gray-400">
                              API: {formatRateDate(cachedInfo.fetchedAt)}
                            </p>
                          )}
                          {!isVef && hasExchangeRateApiKey && (
                            <button
                              onClick={handleRefreshRate}
                              disabled={isRefreshingRate}
                              className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-700 disabled:opacity-50 w-full"
                            >
                              {isRefreshingRate ? (
                                <>
                                  <svg className="animate-spin w-3.5 h-3.5" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                  </svg>
                                  Actualizando...
                                </>
                              ) : (
                                <>
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                  </svg>
                                  Actualizar desde API
                                </>
                              )}
                            </button>
                          )}
                        </>
                      )
                    )}
                  </div>
                </div>
              )}
            </div>
            {/* Theme Toggle - solo desktop */}
            <button
              onClick={toggleDark}
              className="hidden md:flex w-10 h-10 bg-gray-100 dark:bg-gray-800 rounded-xl items-center justify-center hover:bg-gray-200 dark:hover:bg-gray-700 transition-all"
              title={isDark ? "Modo claro" : "Modo oscuro"}
              aria-label={isDark ? "Cambiar a modo claro" : "Cambiar a modo oscuro"}
            >
              {isDark ? (
                <svg className="w-5 h-5 text-gray-700 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              ) : (
                <svg className="w-5 h-5 text-gray-700 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
              )}
            </button>
            {/* Botón para mostrar/ocultar montos - solo desktop */}
            <button 
              onClick={() => dispatch({ type: "SET_SHOW_AMOUNTS", payload: !state.showAmounts })}
              className="hidden md:flex w-10 h-10 bg-gray-100 dark:bg-gray-800 rounded-xl items-center justify-center hover:bg-gray-200 dark:hover:bg-gray-700 transition-all"
              title={state.showAmounts ? "Ocultar montos" : "Mostrar montos"}
              aria-label={state.showAmounts ? "Ocultar montos" : "Mostrar montos"}
            >
              {state.showAmounts ? (
                  <svg className="w-5 h-5 text-gray-700 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5 text-gray-700 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                )}
              </button>
            
            {/* Avatar con dropdown en desktop */}
            <div className="relative hidden md:block" ref={userMenuRef}>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setShowUserMenu(!showUserMenu); }}
                className="flex items-center gap-2 p-1.5 rounded-xl transition-colors hover:bg-gray-100 dark:hover:bg-gray-800"
                aria-label="Menú de usuario"
                aria-expanded={showUserMenu}
              >
                <Avatar src={user?.photoURL} name={user?.name} size="md" />
                <svg className={`w-4 h-4 text-gray-500 dark:text-gray-400 transition-transform ${showUserMenu ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {showUserMenu && (
                <div className="absolute right-0 top-full mt-1 w-48 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 py-2 z-50">
                  <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-700">
                    <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">{user?.name}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{user?.email}</p>
                  </div>
                  <button
                    onClick={() => {
                      setShowUserMenu(false);
                      navigate("/settings");
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Configuración
                  </button>
                  <button
                    onClick={() => {
                      setShowUserMenu(false);
                      logout();
                      navigate("/login");
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    Cerrar sesión
                  </button>
                </div>
              )}
            </div>
            {/* Solo avatar en móvil - enlace a configuración */}
            <button
              onClick={() => navigate("/settings")}
              className="md:hidden p-0"
              aria-label="Ir a configuración"
            >
              <Avatar src={user?.photoURL} name={user?.name} size="md" />
            </button>
          </div>
        </div>

        {/* Menú móvil - altura completa de la ventana */}
        {showMobileMenu && (
          <div className="md:hidden absolute top-full left-0 right-0 min-h-[calc(100dvh-5rem)] bg-white dark:bg-gray-900 shadow-lg border-t border-gray-200 dark:border-gray-800 z-40 overflow-y-auto">
            <nav className="p-4 space-y-1">
              {/* Moneda - solo en móvil */}
              <div className="pb-3 mb-3 border-b border-gray-200 dark:border-gray-700 space-y-2">
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase px-4 py-1">Moneda</p>
                <div className="grid grid-cols-2 gap-2">
                  {currencyOptions.map((opt) => (
                    <button
                      key={opt.code}
                      onClick={() => {
                        if (opt.code === "VEF") {
                          setVefTempRate(String(state.exchangeRates?.VEF ?? 36));
                          setShowVefPopup(true);
                          setShowMobileMenu(false);
                        } else {
                          handleSetCurrency(opt.code);
                          setShowMobileMenu(false);
                        }
                      }}
                      className={`px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                        state.currency === opt.code
                          ? "bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300"
                          : "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              <button
                onClick={() => { navigate("/savings"); setShowMobileMenu(false); }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-left"
              >
                <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="font-medium">Metas de Ahorro</span>
              </button>
              <button
                onClick={() => { navigate("/recurring-incomes"); setShowMobileMenu(false); }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-left"
              >
                <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="font-medium">Ingresos Recurrentes</span>
              </button>
              <button
                onClick={() => { navigate("/recurring"); setShowMobileMenu(false); }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-left"
              >
                <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <span className="font-medium">Gastos Recurrentes</span>
              </button>
              <div className="border-t border-gray-200 dark:border-gray-800 mt-1 pt-3 space-y-1">
                <button
                  onClick={() => toggleDark()}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-left"
                >
                  {isDark ? (
                    <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                    </svg>
                  )}
                  <span className="font-medium">{isDark ? "Modo claro" : "Modo oscuro"}</span>
                </button>
                <button
                  onClick={() => dispatch({ type: "SET_SHOW_AMOUNTS", payload: !state.showAmounts })}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-left"
                >
                  {state.showAmounts ? (
                    <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  )}
                  <span className="font-medium">{state.showAmounts ? "Ocultar montos" : "Mostrar montos"}</span>
                </button>
                <button
                  onClick={() => { navigate("/settings"); setShowMobileMenu(false); }}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-left"
                >
                  <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span className="font-medium">Configuración</span>
                </button>
              </div>
              <button
                onClick={() => { logout(); navigate("/login"); setShowMobileMenu(false); }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-left border-t border-gray-200 dark:border-gray-800 mt-1 pt-3"
              >
                <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                <span className="font-medium">Cerrar sesión</span>
              </button>
            </nav>
          </div>
        )}
      </nav>
      {/* Overlay para cerrar menú al tocar fuera */}
      {showMobileMenu && (
        <div
          className="md:hidden fixed inset-0 bg-black/30 z-30"
          onClick={() => setShowMobileMenu(false)}
          aria-hidden="true"
        />
      )}

      {/* Popup para configurar Bolívares (VE) */}
      {showVefPopup && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50"
          onClick={handleVefPopupCancel}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-6 w-full max-w-sm"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Configurar Bolívares (Venezuela)
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Ingresa cuántos bolívares equivale 1 USD para las conversiones.
            </p>
            <div className="space-y-2 mb-6">
              <label htmlFor="vef-rate-input" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                1 USD = Bs
              </label>
              <input
                id="vef-rate-input"
                type="number"
                step="1"
                min="1"
                value={vefTempRate}
                onChange={(e) => setVefTempRate(e.target.value)}
                placeholder="Ej: 36"
                className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-xl focus:outline-none focus:border-blue-500"
                autoFocus
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleVefPopupSave}
                className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-colors"
              >
                Guardar
              </button>
              <button
                onClick={handleVefPopupCancel}
                className="flex-1 px-4 py-3 bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-100 font-medium rounded-xl transition-colors"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      <main className="px-6 md:px-8 py-8 md:py-12">{children}</main>
    </div>
  );
};

Layout.propTypes = {
  children: PropTypes.node.isRequired
};

export default Layout;
